import { useCallback } from 'react';

import {
  UserConfirmationData,
  CancelToken,
  UserConfirmationResultType,
  BackendErrorCode,
  assertExhaustive,
  AuthenticatorType,
  sleep,
  promiseWithResolvers,
  trackError,
  suppressError,
} from 'backend-client-web';
import { ClientCore } from 'client-core';
import {
  UnlockDeviceType,
  extractCredentialGetResult,
  getNavigatorCredentials,
} from 'client-web-sdk';
import { DomainError } from 'domain-errors';

import { getAccountWebauthnStatus } from './useIsWebauthnOnly';
import useUnlockDeviceType from './useUnlockDeviceType';

export enum RequestUserConfirmationResult {
  accepted,
  rejected,
  timeout,
  canceled,
}

export type RequestUserConfirmation = (
  data: UserConfirmationData,
  cancelToken?: CancelToken,
) => Promise<RequestUserConfirmationResult>;

const TIMEOUT_MS = 30_000;

export default function useRequestUserConfirmation(
  clientCore: ClientCore,
  { requirePushAuthenticatorIfAvailable }: { requirePushAuthenticatorIfAvailable?: boolean } = {},
): { requestUserConfirmation: RequestUserConfirmation; unlockDeviceType: UnlockDeviceType } {
  const requestUserConfirmation = useCallback(
    async (
      data: UserConfirmationData,
      cancelToken?: CancelToken,
    ): Promise<RequestUserConfirmationResult> => {
      // Special case for dummy auth users, just skip confirmation and return immediately.
      const currentAuth = clientCore.params.unlockedAuthenticator.authenticator;
      if (currentAuth.authenticatorType === AuthenticatorType.DUMMY) {
        // sleep a little to make the ui act realistically
        await sleep(1_000);
        return RequestUserConfirmationResult.accepted;
      }

      const { isWebauthnOnly } = getAccountWebauthnStatus(clientCore.accountState);

      const webauthnAbortController = new AbortController();

      async function requestWebauthn() {
        const options = request.webauthnOptions;
        if (!options) {
          return;
        }
        options.signal = webauthnAbortController.signal;
        const navigatorCredentials = getNavigatorCredentials();
        if (!navigatorCredentials) {
          return;
        }
        try {
          const cred = await navigatorCredentials.get(options);
          const webauthnResponse = extractCredentialGetResult(cred);
          await clientCore.onlineUserConfirmationSendWebauthnResult(
            request.id,
            UserConfirmationResultType.ACCEPTED,
            webauthnResponse,
          );
        } catch (e) {
          if (e instanceof DOMException) {
            if (e.name === 'AbortError' || e.name === 'NotAllowedError') {
              // Happens when cancelled via AbortController or by user. If the user if webauthn
              // only, we can cancel to whole shebang since there's no other way to confirm. If the
              // user also has a push auth they can confirm via that so we just ignore the
              // cancellation.
              if (isWebauthnOnly) {
                cancel();
              }
              return;
            }
          }
          throw e;
        }
      }

      const request = await clientCore.onlineUserConfirmationCreate(data);
      if (!requirePushAuthenticatorIfAvailable || isWebauthnOnly) {
        requestWebauthn().catch(trackError);
      }

      const { promise, resolve, reject } = promiseWithResolvers<RequestUserConfirmationResult>();

      function cleanup() {
        webauthnAbortController.abort();
        clientCore.off('syncEnd', handleSyncEnd);
        cancelToken?.offCancel(cancel);
        window.clearTimeout(timeoutId);
      }

      function handleSyncEnd() {
        if (clientCore.accountState.openUserConfirmationIds.includes(request.id)) {
          return;
        }
        (async () => {
          let result: UserConfirmationResultType;

          try {
            result = await clientCore.onlineUserConfirmationRetrieveResult(request.id);
          } catch (e) {
            if (
              e instanceof DomainError &&
              e.code === BackendErrorCode.USER_CONFIRMATION_NOT_FOUND &&
              cancelToken?.isCancelled
            ) {
              return;
            }
            throw e;
          }

          if (result === UserConfirmationResultType.OPEN) {
            return;
          }
          if (result === UserConfirmationResultType.ACCEPTED) {
            resolve(RequestUserConfirmationResult.accepted);
            cleanup();
            return;
          }
          if (result === UserConfirmationResultType.REJECTED) {
            resolve(RequestUserConfirmationResult.rejected);
            cleanup();
            return;
          }

          assertExhaustive(result);
        })().catch((reason) => reject(reason));
      }

      function cancel() {
        (async () => {
          // try to cancel confirmation, ignore error (e.g. if already confirmed or cancelled)
          clientCore.onlineUserConfirmationCancel(request.id).catch(suppressError);
          resolve(RequestUserConfirmationResult.canceled);
          cleanup();
        })().catch((reason) => reject(reason));
      }

      clientCore.on('syncEnd', handleSyncEnd);
      cancelToken?.onCancel(cancel);

      const timeoutId = window.setTimeout(() => {
        // try to cancel confirmation, ignore error (e.g. if already confirmed or cancelled)
        clientCore.onlineUserConfirmationCancel(request.id).catch(suppressError);
        resolve(RequestUserConfirmationResult.timeout);
        cleanup();
      }, TIMEOUT_MS);

      return promise;
    },
    [clientCore, requirePushAuthenticatorIfAvailable],
  );

  const unlockDeviceType = useUnlockDeviceType(clientCore);

  return {
    requestUserConfirmation,
    unlockDeviceType:
      requirePushAuthenticatorIfAvailable && unlockDeviceType === 'multiple' ?
        'phone'
      : unlockDeviceType,
  };
}
